import { useEffect, useState } from "react";

import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { EditMode } from "@/Common/Models/Patient/PatientEnums";
import { IPatientRelationshipsModel } from "@/Common/Models/Patient/UI/Profile/IPatientRelationshipsModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { AddRelationPopup } from "./AddRelationPopup";
import { RelationEdit } from "./RelationEdit";
import { RelationView } from "./RelationView";

import styles from "./Relations.module.scss";

interface IRelationsProps {
    model: IPatientRelationshipsModel;
    patientModel: Patient;
    view: EditMode;
}

function Relations(props: IRelationsProps): JSX.Element {
    const { getText } = useTranslation();

    const [showRelationshipPopup, setShowRelationshipPopup] = useState(false);

    useEffect(() => {
        $(window).trigger("resize");
    });

    const onAddRelation = (linkedContact: LinkedContact): void => {
        sessionStore.dispatch(LocalPatientOperations.Profile.Relationships_AddRelation, { linkedContact });
        setShowRelationshipPopup(false);
    };

    const onRemoveRelation = (linkedContact: LinkedContact, idx: number): void => {
        sessionStore.dispatch(LocalPatientOperations.Profile.Relationships_RemoveClick, {
            linkedContactId: linkedContact.Id,
            itemIndex: idx
        });
    };

    const onChangeRelation = (index: number, mutation: (linkedContact: LinkedContact) => void): void => {
        sessionStore.dispatch(LocalPatientOperations.Profile.Relationships_RelationshipLinkedContactMutation, {
            linkedContactId: props.model.get("Relationships").get(index).Id,
            mutation: mutation
        });
    };

    const existingRelationsContactIds = (): string[] => {
        const contactsIds = props.model.get("Relationships").map(r => r.Individual.Contact_id).toArray();
        contactsIds.push(props.patientModel.IndividualResponse.Contact_id);
        return contactsIds;
    };

    return (
        <div className="patient-prof scrollable">
            {showRelationshipPopup && (
                <AddRelationPopup
                    onClose={() => setShowRelationshipPopup(false)}
                    onSave={linkedContact => onAddRelation(linkedContact)}
                    existingRelationsContactIds={existingRelationsContactIds()}
                />
            )}
            <div className={`block separated ${styles.relations}`}>
                <header>
                    <div className={styles.header + " heading"}>
                        <h3 className="thin-font">{getText("RelationsLabel")}</h3>
                        {patientStore.CanEditPatientRelationship && (
                            <a onClick={() => setShowRelationshipPopup(true)} className={styles.addButton} data-cy="patientProfile-relations-add">[+] {getText("Relations_AddRelationLabel")}</a>
                        )}
                    </div>
                </header>
                <div className={styles.bodyList}>
                    <div className={styles.list}>
                        {props.model.get("Relationships").toArray().map((linkedContact, index) => (
                            <>
                                {props.view === EditMode.Edit && (
                                    <RelationEdit
                                        index={index}
                                        linkedContact={linkedContact}
                                        onRemoveRelation={() => onRemoveRelation(linkedContact, index)}
                                        onChanged={mutation => onChangeRelation(index, mutation)}
                                        key={linkedContact.Id}
                                    />
                                )}
                                {props.view !== EditMode.Edit && (
                                    <RelationView
                                        linkedContact={linkedContact}
                                        key={linkedContact.Id}
                                    />
                                )}
                            </>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export { Relations };
